import type { RoleConfig } from '@/lib/roleConfig';

// Frappe's json_error wraps the message in its own envelope.
// Response shape can be either:
//   { data: { message: "..." } }          ← json_error via frappe.response
//   { message: { message: "..." } }       ← Frappe default whitelist wrapper
//   { message: "plain string" }           ← some Frappe versions
function extractErrorMessage(result: any, fallback: string): string {
  return (
    result?.data?.message ??
    result?.message?.message ??
    (typeof result?.message === 'string' ? result.message : null) ??
    fallback
  );
}

export const fetchRoleConfig = async (): Promise<RoleConfig> => {
  const res = await fetch('/api/v1/role-config', {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) throw new Error('Failed to fetch role config');
  const result = await res.json();
  const data = result?.data ?? result;
  if (!data?.roles || !data?.route_permissions) {
    throw new Error('Invalid role config response');
  }
  return data as RoleConfig;
};

export const saveRoutePermission = async (payload: {
  name?: string | null;
  route: string;
  label: string;
  is_active: number;
  allowed_roles: string[];
}): Promise<{ name: string }> => {
  const res = await fetch('/api/v1/role-config/save-route-permission', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const result = await res.json();
  if (!res.ok) {
    throw new Error(extractErrorMessage(result, 'Failed to save route permission'));
  }
  return result?.data ?? result;
};

export const deleteRoutePermission = async (name: string): Promise<void> => {
  const res = await fetch('/api/v1/role-config/delete-route-permission', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  const result = await res.json();
  if (!res.ok) {
    throw new Error(extractErrorMessage(result, 'Failed to delete route permission'));
  }
};

export const deleteAppRole = async (name: string): Promise<void> => {
  const res = await fetch('/api/v1/role-config/delete-app-role', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  const result = await res.json();
  if (!res.ok) {
    throw new Error(extractErrorMessage(result, 'Failed to delete app role'));
  }
};

export const saveAppRole = async (payload: {
  name?: string | null;
  role_key: string;
  label: string;
  default_redirect: string;
  is_active: number;
}): Promise<{ name: string }> => {
  const res = await fetch('/api/v1/role-config/save-app-role', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const result = await res.json();
  if (!res.ok) {
    throw new Error(extractErrorMessage(result, 'Failed to save app role'));
  }
  return result?.data ?? result;
};
